// ABOUTME: Save file for user layout preferences — named column configs per gridKey, with a reserved tabConfigs field for the future.
// ABOUTME: Touched only on explicit user action (save, load, rename, delete, set-active, acknowledge-new-columns). Renderer never reads this slice.

import { createSlice } from "@reduxjs/toolkit";
import {
    shDatagridPageSizeOptions,
} from "../filter/DataGridGeneralOptions";

/**
 * userLayoutSlice document shape:
 *
 * {
 *   columnConfigs: {
 *     [gridKey]: {
 *       activeConfigId: string,       // config currently being VIEWED — changes on every dropdown click
 *       defaultConfigId: string,      // config that auto-loads on mount — changes only via "set as active"
 *       configs: {
 *         [configId]: {
 *           id: string,
 *           name: string,
 *           columns: [                 // ordered — first entry is leftmost
 *             { field: string, visible: boolean, pinned: "left" | "right" | null }
 *           ],
 *           filters: object | null,    // optional snapshot of gridFilterSlice filter at save time
 *           sortModel: object | null,  // optional snapshot of gridFilterSlice sortModel at save time
 *           groupBy: { field: string, granularity: string | null } | null, // row-grouping state
 *           rowHeight: number | "auto" | null, // per-config row height override
 *           acknowledgedColumns: string[], // field names the user has seen via the + popover
 *           createdAt: string,         // ISO
 *           updatedAt: string          // ISO
 *         }
 *       }
 *     }
 *   },
 *
 *   generalSettings: {
 *     // Cross-grid user preferences that don't belong to any specific gridKey.
 *     // Persistent source of truth — runtime equivalents in gridFilterSlice.settings
 *     // are seeded from here by the mount-flow (see useGridLayout.applySavedConfig).
 *     pageSize: number,                 // rows per page
 *   },
 *
 *   tabConfigs: {
 *     // Per-screen pinned view tabs (e.g. the warehouse orders screen).
 *     // pinnedKeys are view keys from that screen's tab config, stored in the
 *     // order the UI resolves them (canonical config order).
 *     [tabKey]: { pinnedKeys: string[] }
 *   }
 * }
 *
 * Family / "save for all":
 *   Family membership is NOT stored here. The developer declares a static array of
 *   sibling gridKeys and passes it as the `family` prop on each sibling <ShDataGrid3>.
 *   When the user saves with "save for all" ticked, the save flow reads that prop
 *   directly and calls upsertConfig (or applyConfigToFamily) for each gridKey in
 *   the array. No registration on mount, no persistent sibling tracking.
 */

const DEFAULT_ACTIVE_CONFIG_ID = "default";

const emptyGeneralSettings = () => ({
    pageSize: shDatagridPageSizeOptions[0],
    // ShDataGrid3 has its own pageSize so it can go above 100 without
    // crashing ShDataGrid2 (MUI DataGrid can't handle >100 rows).
    pageSize3: shDatagridPageSizeOptions[0],
});

const emptyDocument = () => ({
    columnConfigs: {},
    generalSettings: emptyGeneralSettings(),
    tabConfigs: {},
});

const ensureGridEntry = (state, gridKey) => {
    if (!state.document.columnConfigs[gridKey]) {
        state.document.columnConfigs[gridKey] = {
            activeConfigId: DEFAULT_ACTIVE_CONFIG_ID,
            defaultConfigId: DEFAULT_ACTIVE_CONFIG_ID,
            configs: {},
        };
    }
    if (!state.document.columnConfigs[gridKey].defaultConfigId) {
        state.document.columnConfigs[gridKey].defaultConfigId = DEFAULT_ACTIVE_CONFIG_ID;
    }
    return state.document.columnConfigs[gridKey];
};

const initialState = {
    document: emptyDocument(),
    // Set by the tableSettingsMiddleware when an environment switch returns
    // no remote config while the browser has a local config. The UI reads
    // this to show a confirmation modal prompting the user to save.
    promptSaveToNewEnv: false,
    // Snapshot of the local document that was present before the env switch.
    // Used by the confirmation modal to know what to PUT if the user confirms.
    pendingDocument: null,
};

const userLayoutSlice = createSlice({
    name: "userLayout",
    initialState,
    reducers: {
        /**
         * Apply a full document. Used by future backend fetch, tests, and manual resets.
         * Not used by Redux Persist rehydration — that happens automatically via the
         * slice's initialState merge on REHYDRATE, no reducer needed.
         */
        hydrate: (state, action) => {
            const doc = action.payload;
            state.document = {
                columnConfigs: doc?.columnConfigs ?? {},
                generalSettings: {
                    ...emptyGeneralSettings(),
                    ...(doc?.generalSettings ?? {}),
                },
                tabConfigs: doc?.tabConfigs ?? {},
            };
        },

        /**
         * Write a single general-setting field. Used for cross-grid preferences like
         * pageSize and rowHeight — things that aren't tied to a specific gridKey.
         * Payload: { key, value }. The key must be one the UI knows how to consume
         * (currently "pageSize" or "rowHeight"); unknown keys are still written
         * defensively so future fields slot in without a reducer change.
         */
        setGeneralSetting: (state, action) => {
            const { key, value } = action.payload;
            if (!key) return;
            if (!state.document.generalSettings) {
                state.document.generalSettings = emptyGeneralSettings();
            }
            state.document.generalSettings[key] = value;
        },

        /**
         * Create or update a named config under a gridKey.
         * Payload: { gridKey, config } where config has { id, name, columns, filters?, sortModel? }.
         * If the config id already exists it's replaced; otherwise it's created.
         * createdAt/updatedAt are stamped by this reducer.
         * If this is the first config for the gridKey, it is also set as active.
         */
        upsertConfig: (state, action) => {
            const { gridKey, config } = action.payload;
            if (!gridKey || !config?.id) return;

            const gridEntry = ensureGridEntry(state, gridKey);
            const now = new Date().toISOString();
            const existing = gridEntry.configs[config.id];

            gridEntry.configs[config.id] = {
                id: config.id,
                name: config.name ?? existing?.name ?? "Untitled",
                columns: Array.isArray(config.columns) ? config.columns : (existing?.columns ?? []),
                filters: config.filters ?? null,
                sortModel: config.sortModel ?? null,
                groupBy: config.groupBy ?? null,
                rowHeight: config.rowHeight ?? existing?.rowHeight ?? null,
                acknowledgedColumns: existing?.acknowledgedColumns ?? [],
                // Siblings this config's pointer entries were written to. Owner-side
                // record of the share. Null/absent = not shared. Updated explicitly
                // on save; preserved across updates unless the caller passes a new value.
                sharedWith: Array.isArray(config.sharedWith)
                    ? config.sharedWith
                    : (existing?.sharedWith ?? null),
                createdAt: existing?.createdAt ?? now,
                updatedAt: now,
            };

            // If no active config yet, auto-activate this new one.
            if (gridEntry.activeConfigId === DEFAULT_ACTIVE_CONFIG_ID) {
                gridEntry.activeConfigId = config.id;
            }
        },

        /**
         * Delete a config under a gridKey.
         * Payload: { gridKey, configId }.
         * If the deleted config was active, activeConfigId falls back to "default".
         */
        deleteConfig: (state, action) => {
            const { gridKey, configId } = action.payload;
            const gridEntry = state.document.columnConfigs[gridKey];
            if (!gridEntry || !gridEntry.configs[configId]) return;

            delete gridEntry.configs[configId];

            if (gridEntry.activeConfigId === configId) {
                gridEntry.activeConfigId = DEFAULT_ACTIVE_CONFIG_ID;
            }

            // If this was the last config, optionally remove the gridEntry entirely to keep the document tidy.
            if (Object.keys(gridEntry.configs).length === 0) {
                delete state.document.columnConfigs[gridKey];
            }
        },

        /**
         * Rename an existing config.
         * Payload: { gridKey, configId, name }.
         */
        renameConfig: (state, action) => {
            const { gridKey, configId, name } = action.payload;
            const config = state.document.columnConfigs[gridKey]?.configs[configId];
            if (!config || typeof name !== "string" || name.length === 0) return;
            config.name = name;
            config.updatedAt = new Date().toISOString();
        },

        /**
         * Set which config is currently being viewed. Changes on every dropdown
         * click. Does NOT affect which config auto-loads on mount (that's
         * defaultConfigId via setDefaultConfig).
         * Payload: { gridKey, configId } where configId can be "default" to reset.
         */
        setActiveConfig: (state, action) => {
            const { gridKey, configId } = action.payload;
            const gridEntry = ensureGridEntry(state, gridKey);

            if (configId === DEFAULT_ACTIVE_CONFIG_ID) {
                gridEntry.activeConfigId = DEFAULT_ACTIVE_CONFIG_ID;
                return;
            }

            // Accept any configId: it may be local (in own configs) OR a pointer to
            // a config owned by a family sibling. The slice can't verify the pointer
            // target without scanning other gridKeys; the hook's resolvePointer handles
            // that at read time. If the UI dispatched this id, it has a reason.
            gridEntry.activeConfigId = configId;
        },

        /**
         * Set which config auto-loads on mount (the user's intentional "go-to"
         * view). Only changed via the "set as active" checkbox in the save
         * dialog, never by dropdown clicks.
         * Payload: { gridKey, configId } where configId can be "default" to unset.
         */
        setDefaultConfig: (state, action) => {
            const { gridKey, configId } = action.payload;
            const gridEntry = ensureGridEntry(state, gridKey);

            if (configId === DEFAULT_ACTIVE_CONFIG_ID) {
                gridEntry.defaultConfigId = DEFAULT_ACTIVE_CONFIG_ID;
                return;
            }

            // Same as setActiveConfig: accept pointer configIds too.
            gridEntry.defaultConfigId = configId;
        },

        /**
         * Install pointer entries on sibling gridKeys so they resolve to a config
         * owned by another gridKey. The config body is NOT duplicated — only
         * `activeConfigId` is set, pointing at the owner's configId. At mount time
         * `useGridLayout` detects the pointer (activeConfigId set, but the config
         * is absent from the sibling's own `configs`) and resolves it against the
         * owner's `configs[id]` via the owner's `sharedWith` array.
         *
         * Payload: { configId, siblingGridKeys: [gridKey, ...] }.
         * The owner's config (including its `sharedWith` list) is written separately
         * via `upsertConfig` — this reducer does NOT touch the owner.
         */
        applyConfigToFamily: (state, action) => {
            const { configId, siblingGridKeys } = action.payload;
            if (!configId || !Array.isArray(siblingGridKeys)) return;

            for (const siblingKey of siblingGridKeys) {
                const gridEntry = ensureGridEntry(state, siblingKey);
                // Pointer entry: activeConfigId points to the shared config; `configs`
                // stays empty so the resolution path knows this gridKey doesn't own it.
                gridEntry.activeConfigId = configId;
            }
        },

        /**
         * Detach a shared config from this gridKey without deleting the owner's copy.
         * Used when a sibling wants to stop using a shared view ("unmount" action).
         * Payload: { gridKey, configId }.
         *
         * Effects:
         *  - Remove `gridKey` from the owner's `configs[configId].sharedWith` array.
         *  - Reset this gridKey's pointer entry (`activeConfigId` + `defaultConfigId`
         *    back to "default"). If nothing else was stored, the entry is removed.
         *
         * Other siblings and the owner are unaffected.
         */
        unshareConfigForGrid: (state, action) => {
            const { gridKey, configId } = action.payload;
            if (!gridKey || !configId) return;

            // Find the owner: some other gridKey whose configs map includes configId
            // and whose sharedWith array lists this gridKey. Bounded by the doc size.
            for (const [ownerKey, ownerEntry] of Object.entries(state.document.columnConfigs)) {
                if (ownerKey === gridKey) continue;
                const cfg = ownerEntry?.configs?.[configId];
                if (!cfg || !Array.isArray(cfg.sharedWith)) continue;
                if (!cfg.sharedWith.includes(gridKey)) continue;
                cfg.sharedWith = cfg.sharedWith.filter((k) => k !== gridKey);
                cfg.updatedAt = new Date().toISOString();
                break;
            }

            // Reset this gridKey's pointer. Keep the entry around only if it also
            // owns other configs locally; otherwise remove it to keep the doc tidy.
            const selfEntry = state.document.columnConfigs[gridKey];
            if (!selfEntry) return;
            if (selfEntry.activeConfigId === configId) {
                selfEntry.activeConfigId = DEFAULT_ACTIVE_CONFIG_ID;
            }
            if (selfEntry.defaultConfigId === configId) {
                selfEntry.defaultConfigId = DEFAULT_ACTIVE_CONFIG_ID;
            }
            if (
                Object.keys(selfEntry.configs || {}).length === 0 &&
                selfEntry.activeConfigId === DEFAULT_ACTIVE_CONFIG_ID &&
                selfEntry.defaultConfigId === DEFAULT_ACTIVE_CONFIG_ID
            ) {
                delete state.document.columnConfigs[gridKey];
            }
        },

        /**
         * Mark a set of column fields as "seen" by the user for the active config on a gridKey.
         * Clears the new-column indicator dot.
         * Payload: { gridKey, fields: [fieldName, ...] }.
         */
        acknowledgeNewColumns: (state, action) => {
            const { gridKey, fields } = action.payload;
            const gridEntry = state.document.columnConfigs[gridKey];
            if (!gridEntry) return;
            const activeId = gridEntry.activeConfigId;
            if (activeId === DEFAULT_ACTIVE_CONFIG_ID) return;
            const config = gridEntry.configs[activeId];
            if (!config || !Array.isArray(fields)) return;
            config.acknowledgedColumns = [...fields];
            config.updatedAt = new Date().toISOString();
        },

        /**
         * Store the set of pinned view keys for a tabbed screen.
         * Payload: { tabKey, pinnedKeys: string[] }. Non-string entries are dropped
         * and duplicates removed; an empty array is valid (user unpinned everything).
         * There is no maximum — how many tabs fit is the user's own call.
         */
        setPinnedViews: (state, action) => {
            const { tabKey, pinnedKeys } = action.payload ?? {};
            if (!tabKey || !Array.isArray(pinnedKeys)) return;
            if (!state.document.tabConfigs) {
                state.document.tabConfigs = {};
            }
            const cleaned = [...new Set(pinnedKeys.filter((key) => typeof key === "string"))];
            state.document.tabConfigs[tabKey] = { pinnedKeys: cleaned };
        },

        /**
         * Signal the UI to show the "save config to new environment?" modal.
         * Payload: { pendingDocument } — the local document snapshot to save.
         */
        showSaveToNewEnvPrompt: (state, action) => {
            state.promptSaveToNewEnv = true;
            state.pendingDocument = action.payload?.pendingDocument ?? null;
        },

        /**
         * Dismiss the prompt (user confirmed or cancelled).
         */
        dismissSaveToNewEnvPrompt: (state) => {
            state.promptSaveToNewEnv = false;
            state.pendingDocument = null;
        },

        /**
         * Wipe the whole document. Used for logout / "reset all my layouts" flows.
         */
        clearAll: (state) => {
            state.document = emptyDocument();
            state.promptSaveToNewEnv = false;
            state.pendingDocument = null;
        },
    },
});

export const {
    hydrate,
    upsertConfig,
    deleteConfig,
    renameConfig,
    setActiveConfig,
    setDefaultConfig,
    applyConfigToFamily,
    unshareConfigForGrid,
    acknowledgeNewColumns,
    setGeneralSetting,
    setPinnedViews,
    showSaveToNewEnvPrompt,
    dismissSaveToNewEnvPrompt,
    clearAll,
} = userLayoutSlice.actions;

export { DEFAULT_ACTIVE_CONFIG_ID };

export default userLayoutSlice;
